import React, { ReactNode, useMemo, useState } from 'react';
import { Grid, Button } from '@mui/material';
import Divider from '@mui/material/Divider';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import styled from 'styled-components';
import SectionHeader from 'components/SectionHeader/SectionHeader';
import { Div } from 'components/Elements/Div/Div';
import LoadingElement from 'components/LoadingElement/LoadingElement';
import ContentWrapper from '../StyledComponents/ContentWrapper';

const StepNavItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  fontsize: 1.125rem;
  min-width: 7.25rem;
`;

const WizardHeader = styled.div`
  background: ${({ theme }) => theme.headerGradient};
  width: calc(100% + 2rem);
  margin-right: -1rem;
  margin-top: -1rem;
  min-height: 7rem;
  padding-top: 1rem;
  div {
    div {
      font-size: 2rem !important;
    }
  }
`;

const StepNavNumber = styled.div<{ $active?: boolean }>`
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 100%;
  font-size: 1.125rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${(props) => props.theme.colors.disabled};

  ${(props) =>
    props.$active &&
    `
    background-color: ${props.theme.colors.blue300};
  `}
`;

const defaultValidator = () => true;

export interface WizardStepInterface {
  title: ReactNode;
  children: ReactNode;
  submitValidator?: () => boolean;
}

interface WizardProps {
  title: ReactNode;
  steps: WizardStepInterface[];
  onSubmit: () => void;
  submitting?: boolean;
  onSaveDraft?: () => void;
  onCancel?: () => void;
}

const Wizard = ({ title, steps, onSubmit, submitting, onCancel, onSaveDraft }: WizardProps) => {
  const [activeStep, setActiveStep] = useState(0);
  const stepLength = steps.length - 1;

  const moveStep = (next: boolean) => {
    window.scrollTo(0, 0);
    if (next && activeStep !== stepLength) {
      setActiveStep(activeStep + 1);
    } else if (!next && activeStep !== 0) {
      setActiveStep(activeStep - 1);
    }
  };

  const submitValidator: () => boolean = steps[activeStep]?.submitValidator || defaultValidator;
  const isInvalid = useMemo(() => !submitValidator(), [submitValidator]);

  return (
    <>
      <SectionHeader title={title} padding={'3.5rem 1rem 1.5rem'} pageHeader addGradient />
      <ContentWrapper>
        <Grid container sx={{ marginTop: '2rem' }}>
          {/* Step Nav */}
          <Grid item xs={12} mt={1}>
            <Div display='flex' width='100%' justifyContent='center'>
              <Div width='100%' display='flex' justifyContent='center'>
                {steps.map((step, i) => (
                  <React.Fragment key={step.title?.toString()}>
                    {i > 0 && (
                      <Divider
                        flexItem
                        sx={{
                          alignSelf: 'center',
                          flexShrink: '1',
                          width: '9rem',
                          margin: '-1.5rem -2rem 0',
                        }}
                      />
                    )}
                    <StepNavItem>
                      <StepNavNumber $active={activeStep === i}>{i + 1}</StepNavNumber>
                      {step.title}
                    </StepNavItem>
                  </React.Fragment>
                ))}
              </Div>
            </Div>
          </Grid>
          {/* Step Children */}
          <Grid item xs={12}>
            {steps[activeStep]?.children}
          </Grid>
          <Grid item xs={12} pt={3} mt={3}>
            <Div display='flex' justifyContent='space-between'>
              <div>
                {onCancel && (
                  <Button onClick={onCancel} variant='text'>
                    Cancel
                  </Button>
                )}
                &nbsp;
                {onSaveDraft && (
                  <Button onClick={onSaveDraft} variant='text'>
                    Save Draft
                  </Button>
                )}
              </div>
              <Div display='flex' alignItems='flex-end' gap='0.75rem'>
                <Button
                  onClick={() => moveStep(false)}
                  disabled={activeStep === 0}
                  color='secondary'
                >
                  <ArrowBackIcon sx={{ marginRight: '0.5rem' }} />
                  Previous
                </Button>
                {activeStep === stepLength ? (
                  <LoadingElement
                    loading={submitting}
                    element={
                      <Button
                        variant={!isInvalid ? 'contained' : undefined}
                        color='secondary'
                        onClick={onSubmit}
                        disabled={isInvalid}
                      >
                        Submit
                      </Button>
                    }
                  />
                ) : (
                  <Button
                    onClick={() => moveStep(true)}
                    disabled={activeStep === stepLength || isInvalid}
                    variant={!isInvalid ? 'contained' : undefined}
                    color='secondary'
                  >
                    Next
                    <ArrowForwardIcon sx={{ marginLeft: '0.5rem' }} />
                  </Button>
                )}
              </Div>
            </Div>
          </Grid>
        </Grid>
      </ContentWrapper>
    </>
  );
};

export default Wizard;
